import { useLocation, Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useState } from 'react';
import { ArrowLeft, Globe } from 'lucide-react';
import './LegalPage.css';

type LegalType = 'imprint' | 'privacy' | 'terms';

/* ═══════════════════════════════════════════
   LEGAL TEXT CONTENT — DE + EN
   ═══════════════════════════════════════════ */

export const LEGAL_CONTENT: Record<LegalType, { de: { title: string; body: string }; en: { title: string; body: string } }> = {
    imprint: {
        de: {
            title: 'Impressum',
            body: `
## Angaben gemäß § 5 DDG

Sembach Software Solutions<br />
Inhaber: Luis Sembach<br />
Winklerstraße 10<br />
90403 Nürnberg
Deutschland

## Kontakt

E-Mail: contact@stratsync.pro<br />
Telefon: +49 162 9742215

## Verantwortlich für den Inhalt nach § 18 Abs. 2 MStV

Luis Sembach<br />
Winklerstraße 10<br />
90403 Nürnberg

## Verbraucherstreitbeilegung

Wir sind nicht bereit oder verpflichtet, an Streitbeilegungsverfahren vor einer Verbraucherschlichtungsstelle teilzunehmen.

## Haftung für Inhalte

Als Diensteanbieter sind wir gemäß § 7 Abs. 1 TMG für eigene Inhalte auf diesen Seiten nach den allgemeinen Gesetzen verantwortlich. Nach §§ 8 bis 10 TMG sind wir als Diensteanbieter jedoch nicht verpflichtet, übermittelte oder gespeicherte fremde Informationen zu überwachen oder nach Umständen zu forschen, die auf eine rechtswidrige Tätigkeit hinweisen.

## Haftung für Links

Unser Angebot enthält Links zu externen Websites Dritter, auf deren Inhalte wir keinen Einfluss haben. Deshalb können wir für diese fremden Inhalte auch keine Gewähr übernehmen. Für die Inhalte der verlinkten Seiten ist stets der jeweilige Anbieter oder Betreiber der Seiten verantwortlich.

## Urheberrecht

Die durch die Seitenbetreiber erstellten Inhalte und Werke auf diesen Seiten unterliegen dem deutschen Urheberrecht. Die Vervielfältigung, Bearbeitung, Verbreitung und jede Art der Verwertung außerhalb der Grenzen des Urheberrechtes bedürfen der schriftlichen Zustimmung des jeweiligen Autors bzw. Erstellers.
`,
        },
        en: {
            title: 'Imprint',
            body: `
> **Note:** This is a courtesy translation. The German version is legally binding.

## Information pursuant to § 5 DDG (German Telemedia Act)

Sembach Software Solutions<br />
Proprietor: Luis Sembach<br />
Winklerstraße 10<br />
90403 Nuremberg
Germany

## Contact

Email: contact@stratsync.pro<br />
Phone: +49 162 9742215

## Responsible for content pursuant to § 18 (2) MStV

Luis Sembach<br />
Winklerstraße 10<br />
90403 Nuremberg

## Consumer Dispute Resolution

We are neither willing nor obligated to participate in dispute resolution proceedings before a consumer arbitration board.

## Liability for Content

As a service provider, we are responsible for our own content on these pages in accordance with § 7 (1) TMG. However, according to §§ 8 to 10 TMG, we are not obligated to monitor transmitted or stored third-party information.

## Liability for Links

Our website contains links to external third-party websites over whose content we have no influence. We therefore cannot accept any liability for this external content.

## Copyright

The content and works created by the site operators on these pages are subject to German copyright law. Reproduction, editing, distribution, and any kind of use beyond the limits of copyright law require written consent of the respective author or creator.
`,
        },
    },
    privacy: {
        de: {
            title: 'Datenschutzerklärung',
            body: `
## 1. Verantwortlicher

Sembach Software Solutions<br />
Inhaber: Luis Sembach<br />
Winklerstraße 10<br />
90403 Nürnberg
Deutschland<br />
E-Mail: contact@stratsync.pro

## 2. Erhobene Daten

Wir erheben und verarbeiten folgende personenbezogene Daten:

- **Authentifizierungs- und Profildaten:** Faceit-ID, Faceit-Nickname, Faceit-ELO, Faceit-Level, Faceit-Avatar und gegebenenfalls Steam-ID. Die im Faceit-Konto hinterlegte E-Mail-Adresse wird von StratSync weder angefordert noch im Nutzerkonto gespeichert
- **Administrativer Zugang:** Ein pseudonymer Login-Benutzername und ein Passwort-Hash ausschließlich für das gesonderte Site-Admin-Konto
- **Supportdaten:** Inhalt des Supporttickets, Chatnachrichten und gegebenenfalls ein Screenshot. StratSync bietet kein Feld zur Speicherung einer Nutzer-Kontakt-E-Mail an
- **Nutzungsdaten:** Hochgeladene Demo-Dateien, Strategien, Lineup-Konfigurationen, Team-Mitgliedschaften
- **Technische Daten:** IP-Adresse, Browser-Typ, Zugriffszeitpunkte (Server-Logs)
- **Premium- und Zahlungszuordnung:** Einlöse-Codes, Aktivierungszeitstempel sowie bei Stripe-Zahlungen die Stripe-Customer- und Abonnement-ID. Zahlungs- und Rechnungsdaten einschließlich der Zahlungs-E-Mail-Adresse werden direkt durch Stripe erhoben und nicht in die StratSync-Nutzertabelle kopiert. Sie bleiben im Stripe-Händlerkonto und in den dortigen Transaktionsunterlagen für StratSync als Händler einsehbar
- **Widerrufsanfragen:** Name, Abrechnungs-E-Mail, ausgewählter Tarif, angegebener Spieler- oder Teamname, optional Kaufdatum und Stripe-Referenz sowie Eingangs-, Bestätigungs- und Bearbeitungsstatus

## 3. Zweck der Datenverarbeitung

- Bereitstellung und Betrieb der Plattform (Art. 6 Abs. 1 lit. b DSGVO)
- Authentifizierung über Steam und Faceit (Art. 6 Abs. 1 lit. b DSGVO)
- Anzeige von Spielerstatistiken und ELO-Daten (Art. 6 Abs. 1 lit. b DSGVO)
- Verwaltung von Premium-Mitgliedschaften (Art. 6 Abs. 1 lit. b DSGVO)
- Abwicklung kostenpflichtiger Abonnements über Stripe (Art. 6 Abs. 1 lit. b DSGVO sowie einschlägige gesetzliche Aufbewahrungspflichten)
- Entgegennahme, Bestätigung, Prüfung und Dokumentation von Widerrufserklärungen sowie Bearbeitung gesetzlicher Ansprüche (Art. 6 Abs. 1 lit. b und c DSGVO)
- Sicherstellung der IT-Sicherheit (Art. 6 Abs. 1 lit. f DSGVO)

## 4. Dritte und Auftragsverarbeiter

| Dienst | Zweck | Standort |
|--------|-------|----------|
| **Hetzner Online GmbH** | Server-Hosting | Deutschland |
| **Valve/Steam API** | Authentifizierung, Profildaten | USA |
| **Faceit Ltd.** | Authentifizierung, ELO/Match-Daten | UK |
| **GitHub (Microsoft)** | Quellcode-Hosting | USA |
| **Telegram Bot API** | Team-Chat-Integration, Benachrichtigungen | Weltweit (Telegram FZ-LLC, Dubai) |
| **Stripe-Unternehmensgruppe** (je nach Dienst insbesondere Stripe Payments Europe, Limited, Stripe Technology Company, Limited und Stripe Technology Europe, Limited) | Zahlungsabwicklung, Abonnements, Rechnungen und Zahlungsbenachrichtigungen nach Freischaltung des Bezahlbetriebs; Einzelheiten im [Stripe Privacy Center](https://stripe.com/legal/privacy-center) | Irland / weitere Empfänger gemäß Stripe-Dokumentation |

Datentransfers in Drittländer erfolgen auf Grundlage der Standardvertragsklauseln (Art. 46 Abs. 2 lit. c DSGVO).

## 5. Cookies und Local Storage

StratSync verwendet **keine Tracking-Cookies**. Wir verwenden:
- **JWT-Token** im LocalStorage zur Authentifizierung
- **Spracheinstellungen** im LocalStorage
- **UI-Präferenzen** (Theme, Sidebar-Reihenfolge) im LocalStorage

Es findet kein Tracking durch Drittanbieter statt. Es werden keine Analytics-Dienste wie Google Analytics eingesetzt.

## 6. Browser-Erweiterung (Chrome Extension)

Die optionale StratSync Browser-Erweiterung ("Demo Uploader") erfasst auf ausdrückliche Aktion des Nutzers den Download-Link einer CS2-Demo in einem Faceit-Matchroom und übergibt ihn an StratSync zur serverseitigen Verarbeitung.

### 6.1 Von der Erweiterung gespeicherte Daten

Die Erweiterung speichert folgende Daten lokal im Browser-Speicher (chrome.storage.local):

- **Authentifizierungsinformationen:** Ein funktionsbeschränktes Extension-Access-Token mit 15 Minuten Laufzeit und ein ebenfalls funktionsbeschränktes Erneuerungs-Token mit höchstens drei Tagen Laufzeit. Das aktive Web-JWT wird nach ausdrücklicher Zustimmung einmalig aus dem geöffneten StratSync-Tab gelesen, unmittelbar gegen diese Extension-Tokens getauscht und nicht im Erweiterungsspeicher gespeichert
- **Benutzerprofil:** Anzeigename, Avatar-URL, Faceit-ELO und Mitglied-seit-Datum zur Darstellung im Popup
- **Team-Kontext:** ID, Name und Faceit-ID der Teams des Nutzers sowie das aktuell ausgewählte Team
- **Transfer-Status:** Ziel, Match-ID, Fortschritt und Fehlermeldungen eines aktiven oder zuletzt fehlgeschlagenen Transfers

Die Verbindung wird erst nach Klick auf **„Connect & Allow“** hergestellt. Die Extension-Tokens werden ausschließlich gegenüber stratsync.pro verwendet. Das Erneuerungs-Token kann keine regulären API-Endpunkte aufrufen, sondern nur neue, funktionsbeschränkte Extension-Access-Tokens anfordern. Bei Abmeldung, ungültiger Sitzung oder Ablauf werden die lokalen Sitzungs-, Profil-, Team- und Transferdaten gelöscht. Bei Deinstallation entfernt Chrome den Erweiterungsspeicher.

### 6.2 Interaktion mit Faceit

Die Erweiterung interagiert ausschließlich mit Webseiten unter faceit.com, um:
- auf Faceit-Matchroom-Seiten die Match-ID und die für die Demo-Aktion erforderlichen sichtbaren Seitenelemente zu erkennen,
- nach Klick auf „Send to StratSync“ den von Faceit erzeugten, zeitlich begrenzten und signierten Demo-Link zu erfassen und einen lokalen Browser-Download zu verhindern.

Die Erweiterung liest keine Faceit-Passwörter, Login-Cookies oder privaten Nachrichten und lädt die Demo-Datei nicht in den Erweiterungsspeicher herunter.

### 6.3 Datenübermittlung und Zweck

Wenn der Nutzer ein Ziel auswählt, übermittelt die Erweiterung das funktionsbeschränkte Extension-Access-Token im Authorization-Header sowie Match-ID, ausgewählte Team-ID, Zieltyp und signierten Demo-Link verschlüsselt an **https://stratsync.pro**. Das Access-Token wird serverseitig ausschließlich für die Extension-Kontextabfrage, Match-Validierung, Match-Speicherprüfung und Demo-Übergabe akzeptiert. StratSync lädt die Datei anschließend serverseitig vom von Faceit verwendeten Speicherdienst herunter und verarbeitet sie entsprechend dem gewählten Ziel (Match, Team Demo Vault, Opponent Analysis oder Personal Storage).

Der signierte Link wird nicht in Anwendungs-Logs ausgegeben und bei neuen Importaufträgen nur solange vorgehalten, wie dies für Warteschlange und Verarbeitung erforderlich ist; nach einem terminalen Auftragsstatus wird er entfernt. Die importierte Demo und daraus erzeugte Analyseergebnisse werden gemäß der vom Nutzer gewählten Speicherfunktion bis zur Löschung durch den Nutzer beziehungsweise nach den für das Produkt geltenden Kapazitäts- und Aufbewahrungsregeln gespeichert.

Es findet keine Nutzung für Werbung, Profiling außerhalb der StratSync-Funktionen oder Verkauf von Daten statt. Die Nutzung von über Chrome APIs erhaltenen Informationen entspricht der Chrome Web Store User Data Policy einschließlich der Limited-Use-Anforderungen.

## 7. Speicherdauer

- **Authentifizierungs- und Profildaten:** Bis zur Löschung des Accounts
- **Administrativer Zugang:** Bis zur Ersetzung oder Aufhebung des Site-Admin-Zugangs
- **Support-Kontaktdaten:** Bis zur Löschung des Tickets beziehungsweise Accounts, soweit keine gesetzliche Aufbewahrung oder die Bearbeitung beziehungsweise Abwehr von Ansprüchen eine längere Speicherung erfordert
- **Demo-Dateien:** Bis zur manuellen Löschung durch den Nutzer
- **Browser-Erweiterung:** Extension-Access-Token maximal 15 Minuten; funktionsbeschränktes Erneuerungs-Token und übrige Sitzungsdaten maximal drei Tage beziehungsweise bis Ablauf, Abmeldung oder Deinstallation; temporärer Transferstatus bis Abschluss, Fehlerbereinigung oder Abmeldung
- **Server-Logs:** Maximal 30 Tage
- **Premium- und Stripe-Zuordnungsdaten:** Für die Dauer der Mitgliedschaft und danach entsprechend den gesetzlichen Nachweis-, Steuer- und Aufbewahrungspflichten; Zahlungsdaten bei Stripe richten sich zusätzlich nach den dort geltenden Aufbewahrungsregeln
- **Widerrufsanfragen:** Bis zum Abschluss der Bearbeitung und anschließend nur so lange, wie gesetzliche Nachweis-, Aufbewahrungs- oder Verjährungsfristen dies erfordern

## 8. Ihre Rechte (DSGVO Art. 15–21)

Sie haben das Recht auf:
- **Auskunft** über Ihre gespeicherten Daten (Art. 15)
- **Berichtigung** unrichtiger Daten (Art. 16)
- **Löschung** Ihrer Daten (Art. 17)
- **Einschränkung** der Verarbeitung (Art. 18)
- **Datenübertragbarkeit** (Art. 20)
- **Widerspruch** gegen die Verarbeitung (Art. 21)

Zur Ausübung Ihrer Rechte kontaktieren Sie uns unter: contact@stratsync.pro

## 9. Beschwerderecht

Sie haben das Recht, sich bei einer Datenschutz-Aufsichtsbehörde zu beschweren. Die zuständige Aufsichtsbehörde richtet sich nach dem Bundesland des Verantwortlichen.

## 10. Änderungen

Wir behalten uns vor, diese Datenschutzerklärung anzupassen, um sie an geänderte Rechtslagen oder Änderungen des Dienstes anzupassen. Die aktuelle Version finden Sie stets auf dieser Seite.

*Stand: 28. August 2026*
`,
        },
        en: {
            title: 'Privacy Policy',
            body: `
> **Note:** This is a courtesy translation. The German version is legally binding.

## 1. Data Controller

Sembach Software Solutions<br />
Proprietor: Luis Sembach<br />
Winklerstraße 10<br />
90403 Nuremberg
Germany<br />
Email: contact@stratsync.pro

## 2. Data We Collect

We collect and process the following personal data:

- **Authentication and profile data:** Faceit ID, Faceit nickname, Faceit ELO, Faceit level, Faceit avatar and, where available, Steam ID. StratSync neither requests nor stores the email address held in the user's Faceit account
- **Administrative access:** A pseudonymous login username and password hash exclusively for the separate site-admin account
- **Support data:** Support-ticket content, chat messages and an optional screenshot. StratSync does not provide a field for storing a user's contact email address
- **Usage data:** Uploaded demo files, strategies, lineup configurations, team memberships
- **Technical data:** IP address, browser type, access timestamps (server logs)
- **Premium and payment references:** Redemption codes, activation timestamps and, for Stripe payments, the Stripe Customer and subscription IDs. Stripe collects payment and invoice details, including the billing email address, directly; StratSync does not copy that email into its user table. It remains visible to StratSync as the merchant in the Stripe account and Stripe transaction records
- **Withdrawal requests:** Name, billing email, selected plan, stated player or team name, optional purchase date and Stripe reference, plus receipt, confirmation and processing status

## 3. Purpose of Data Processing

- Providing and operating the platform (Art. 6(1)(b) GDPR)
- Authentication via Steam and Faceit (Art. 6(1)(b) GDPR)
- Displaying player statistics and ELO data (Art. 6(1)(b) GDPR)
- Managing premium memberships (Art. 6(1)(b) GDPR)
- Processing paid subscriptions through Stripe (Art. 6(1)(b) GDPR and applicable statutory retention obligations)
- Receiving, confirming, reviewing and documenting declarations of withdrawal and processing statutory claims (Art. 6(1)(b) and (c) GDPR)
- Ensuring IT security (Art. 6(1)(f) GDPR)

## 4. Third Parties and Processors

| Service | Purpose | Location |
|---------|---------|----------|
| **Hetzner Online GmbH** | Server hosting | Germany |
| **Valve/Steam API** | Authentication, profile data | USA |
| **Faceit Ltd.** | Authentication, ELO/match data | UK |
| **GitHub (Microsoft)** | Source code hosting | USA |
| **Telegram Bot API** | Team chat integration, notifications | Worldwide (Telegram FZ-LLC, Dubai) |
| **Stripe group companies** (depending on the service, in particular Stripe Payments Europe, Limited, Stripe Technology Company, Limited and Stripe Technology Europe, Limited) | Payment processing, subscriptions, invoices and payment notifications once paid plans are enabled; details in the [Stripe Privacy Center](https://stripe.com/legal/privacy-center) | Ireland / further recipients documented by Stripe |

Data transfers to third countries are based on Standard Contractual Clauses (Art. 46(2)(c) GDPR).

## 5. Cookies and Local Storage

StratSync does **not** use tracking cookies. We use:
- **JWT tokens** in LocalStorage for authentication
- **Language preferences** in LocalStorage
- **UI preferences** (theme, sidebar order) in LocalStorage

No third-party tracking takes place. No analytics services such as Google Analytics are used.

## 6. Browser Extension (Chrome Extension)

The optional StratSync Browser Extension ("Demo Uploader") captures the download link for a CS2 demo in a Faceit matchroom only after an explicit user action and hands it to StratSync for server-side processing.

### 6.1 Data Stored by the Extension

The extension stores the following data locally in the browser storage (chrome.storage.local):

- **Authentication information:** A function-restricted extension access token with a 15-minute lifetime and a restricted renewal token with a lifetime of no more than three days. After explicit consent, the active web JWT is read once from the open StratSync tab, immediately exchanged for these extension tokens and not stored in extension storage
- **User profile:** Display name, avatar URL, Faceit ELO and member-since date for display in the popup
- **Team context:** The user's team IDs, names and Faceit IDs, plus the currently selected team
- **Transfer status:** Destination, match ID, progress and error details for an active or most recently failed transfer

The connection is established only after the user clicks **“Connect & Allow.”** The extension tokens are used only with stratsync.pro. The renewal token cannot call regular API endpoints and can only obtain another function-restricted extension access token. Logout, an invalid session or expiry clears the locally stored session, profile, team and transfer data. Chrome removes extension storage when the extension is uninstalled.

### 6.2 Interaction with Faceit

The extension interacts exclusively with websites under faceit.com to:
- recognize the match ID and visible page elements required for the demo action on Faceit matchroom pages; and
- after the user clicks “Send to StratSync,” capture the temporary signed demo URL generated by Faceit and prevent a local browser download.

The extension does not read Faceit passwords, login cookies or private messages, and it does not download the demo file into extension storage.

### 6.3 Data Transmission and Purpose

When the user selects a destination, the extension transmits the function-restricted extension access token in the Authorization header together with the match ID, selected team ID, destination type and signed demo URL over encrypted HTTPS to **https://stratsync.pro**. The access token is accepted server-side only for the extension context, match validation, match storage check and demo handoff. StratSync then downloads the file server-to-server from the storage service used by Faceit and processes it for the chosen destination (Match, Team Demo Vault, Opponent Analysis or Personal Storage).

The signed URL is not written to application logs and, for queued imports, is retained only while required for queueing and processing; it is removed after a terminal job state. The imported demo and generated analysis data remain according to the user-selected storage function until user deletion or the applicable product capacity and retention rules.

Data is not sold or used for advertising or profiling outside StratSync functionality. Use of information received from Chrome APIs complies with the Chrome Web Store User Data Policy, including the Limited Use requirements.

## 7. Data Retention

- **Authentication and profile data:** Until account deletion
- **Administrative access:** Until the site-admin login is replaced or revoked
- **Support contact data:** Until the ticket or account is deleted, unless statutory retention or the establishment, exercise or defence of legal claims requires longer storage
- **Demo files:** Until manually deleted by the user
- **Browser extension:** Extension access token for no more than 15 minutes; restricted renewal token and other session data for no more than three days or until expiry, logout or uninstallation; temporary transfer state until completion, error cleanup or logout
- **Server logs:** Maximum 30 days
- **Premium and Stripe reference data:** For the duration of the membership and afterwards in accordance with statutory evidence, tax and retention duties; payment data retained by Stripe is additionally subject to Stripe's retention rules
- **Withdrawal requests:** Until processing is complete and afterwards only as long as statutory evidence, retention or limitation periods require

## 8. Your Rights (GDPR Art. 15–21)

You have the right to:
- **Access** your stored data (Art. 15)
- **Rectification** of inaccurate data (Art. 16)
- **Erasure** of your data (Art. 17)
- **Restriction** of processing (Art. 18)
- **Data portability** (Art. 20)
- **Object** to processing (Art. 21)

To exercise your rights, contact us at: contact@stratsync.pro

## 9. Right to Lodge a Complaint

You have the right to lodge a complaint with a data protection supervisory authority.

## 10. Changes

We reserve the right to update this privacy policy to reflect changes in legal requirements or the service. The current version is always available on this page.

*Last updated: August 23, 2026*
`,
        },
    },
    terms: {
        de: {
            title: 'Allgemeine Geschäftsbedingungen (AGB)',
            body: `
## 1. Geltungsbereich

Diese Allgemeinen Geschäftsbedingungen gelten für die Nutzung der Plattform StratSync (erreichbar unter stratsync.pro), betrieben von Sembach Software Solutions, Inhaber Luis Sembach, Winklerstraße 10, 90403 Nürnberg.

## 2. Registrierung und Konto

- Die Registrierung erfolgt über Steam oder Faceit OAuth.
- Jeder Nutzer darf nur ein Konto erstellen.
- Die Nutzung der Plattform ist erst ab einem Alter von 16 Jahren gestattet. Mit der Registrierung bestätigt der Nutzer, mindestens 16 Jahre alt zu sein.
- Der Nutzer ist für die Sicherheit seiner Zugangsdaten verantwortlich.
- Bei Verdacht auf Missbrauch behalten wir uns vor, Konten zu sperren oder zu löschen.

## 3. Leistungsbeschreibung

StratSync bietet folgende Dienste:
- Team-Management und Kommunikation
- Demo-Analyse und -Speicherung
- Strategieplanung auf interaktiven Karten
- Utility-Lineup-Verwaltung
- Spielserver-Bereitstellung (CS2)
- Liga- und Saisonverfolgung
- Premium-Mitgliedschaft mit erweiterten Funktionen

## 4. Premium-Mitgliedschaft

- StratSync bietet Player Premium für ein Nutzerkonto, Team Premium für ein ausgewähltes Team sowie ein Bündel aus Team Premium und Player Premium für die fünf beim Kauf angezeigten Main-Roster-Spieler an.
- Kostenpflichtige Premium-Pläne werden als monatlich wiederkehrende Abonnements über Stripe abgeschlossen. Der bei Stripe Checkout angezeigte Gesamtpreis, die Währung und der Abrechnungszeitraum sind vor Abschluss verbindlich maßgeblich.
- Das Abonnement beginnt nach erfolgreicher Zahlungsbestätigung. Rechnungen, Zahlungsart und Abonnementverwaltung werden über Stripe bereitgestellt.
- Das Abonnement verlängert sich jeweils um einen weiteren Monat, bis es gekündigt wird. Eine Kündigung ist über die in StratSync verlinkte Stripe-Abonnementverwaltung möglich und wirkt grundsätzlich zum Ende des bereits bezahlten Abrechnungszeitraums; eine sofortige Beendigung aus wichtigem Grund bleibt unberührt.
- Bei ausbleibender oder zurückgebuchter Zahlung können die betroffenen Premium-Funktionen nach Ablauf des bezahlten Zeitraums gesperrt werden.
- Premium-Codes, Punkte-Shop-Prämien und ausdrücklich als solche bezeichnete Kulanz- oder Testfreischaltungen sind keine eigenständigen kostenpflichtigen Abonnements. Ihre Laufzeit richtet sich nach der jeweiligen Anzeige beziehungsweise Freischaltung.
- Gesetzliche Widerrufs-, Gewährleistungs- und Erstattungsrechte bleiben unberührt. Soweit für einen vorzeitigen Beginn digitaler Leistungen eine ausdrückliche Zustimmung und Bestätigung erforderlich ist, wird diese gesondert im Bestellprozess eingeholt.
- Der Nutzer kann eine Widerrufserklärung über die in den Account Settings bei den Abonnementinformationen verlinkte Funktion [„Vertrag widerrufen“](/withdrawal) oder durch eine andere eindeutige Erklärung an contact@stratsync.pro übermitteln. Die Funktion ist zusätzlich direkt unter /withdrawal erreichbar. Eine Begründung ist nicht erforderlich. Der Eingang über die elektronische Funktion wird unverzüglich elektronisch bestätigt.

## 5. Widerrufsbelehrung für Verbraucher

### Widerrufsrecht

Sie haben das Recht, binnen vierzehn Tagen ohne Angabe von Gründen diesen Vertrag zu widerrufen. Die Widerrufsfrist beträgt vierzehn Tage ab dem Tag des Vertragsschlusses.

Um Ihr Widerrufsrecht auszuüben, müssen Sie uns — Sembach Software Solutions, Inhaber Luis Sembach, Winklerstraße 10, 90403 Nürnberg, E-Mail contact@stratsync.pro — mittels einer eindeutigen Erklärung über Ihren Entschluss informieren. Sie können dafür die elektronische Funktion [„Vertrag widerrufen“](/withdrawal) nutzen. Zur Wahrung der Frist reicht es aus, dass Sie die Mitteilung vor Ablauf der Widerrufsfrist absenden.

### Folgen des Widerrufs

Wenn Sie diesen Vertrag widerrufen, erstatten wir alle von Ihnen im Zusammenhang mit dem widerrufenen Vertrag erhaltenen Zahlungen unverzüglich und spätestens binnen vierzehn Tagen ab Eingang Ihres Widerrufs. Für die Rückzahlung verwenden wir grundsätzlich dasselbe Zahlungsmittel wie bei der ursprünglichen Transaktion, sofern nichts anderes vereinbart wurde; es werden keine Entgelte für die Rückzahlung berechnet.

Haben Sie ausdrücklich verlangt, dass die Dienstleistung während der Widerrufsfrist beginnen soll, ist bei einem Widerruf der angemessene Betrag für die bis zum Widerruf bereits erbrachte Leistung zu zahlen, soweit die gesetzlichen Voraussetzungen hierfür vorliegen.

### Muster-Widerrufsformular

Wenn Sie den Vertrag widerrufen wollen, können Sie folgende Angaben an contact@stratsync.pro senden (die Nutzung dieses Musters ist nicht vorgeschrieben): Name und Anschrift, Bezeichnung des Premium-Tarifs beziehungsweise Teams oder Spielerkontos, Datum des Vertragsschlusses und eine eindeutige Erklärung des Widerrufs, datiert am Tag der Absendung.

## 6. Nutzerpflichten

Der Nutzer verpflichtet sich:
- Keine rechtswidrigen Inhalte hochzuladen
- Die Plattform nicht für automatisierte Datenerhebung (Scraping) zu nutzen
- Keine Spielserver für Zwecke außerhalb von CS2 zu missbrauchen
- Die Rechte Dritter zu respektieren (insbesondere Urheberrechte an Demo-Dateien)

## 7. Verfügbarkeit

- Wir bemühen uns um eine hohe Verfügbarkeit, garantieren jedoch keine unterbrechungsfreie Nutzung.
- Wartungsarbeiten werden nach Möglichkeit im Voraus angekündigt.
- Für Datenverluste durch technische Fehler übernehmen wir keine Haftung, sofern kein vorsätzliches oder grob fahrlässiges Verhalten vorliegt.

## 8. Haftungsbeschränkung

- Die Nutzung erfolgt auf eigenes Risiko.
- Wir haften nicht für Schäden, die durch die Nutzung von Spielservern, Demo-Analysen oder Drittanbieter-APIs (Steam, Faceit) entstehen.
- Die Haftung ist auf Vorsatz und grobe Fahrlässigkeit beschränkt.
- Bei leichter Fahrlässigkeit haften wir nur bei Verletzung wesentlicher Vertragspflichten, begrenzt auf den vorhersehbaren, vertragstypischen Schaden.

## 9. Kündigung

- Der Nutzer kann sein Konto jederzeit löschen.
- Kostenpflichtige Premium-Abonnements können unabhängig von einer Kontolöschung über die verlinkte Stripe-Abonnementverwaltung gekündigt werden.
- Wir behalten uns vor, Konten bei Verstoß gegen diese AGB ohne Vorankündigung zu sperren.
- Bei Kündigung werden personenbezogene Daten gemäß unserer Datenschutzerklärung gelöscht.

## 10. Änderungen der AGB

- Wir behalten uns vor, diese AGB jederzeit zu ändern.
- Änderungen werden dem Nutzer bei der nächsten Anmeldung mitgeteilt.
- Die fortgesetzte Nutzung nach Änderung gilt als Zustimmung.

## 11. Anwendbares Recht und Gerichtsstand

- Es gilt das Recht der Bundesrepublik Deutschland.
- Gerichtsstand ist, soweit gesetzlich zulässig, der Sitz des Betreibers.

*Stand: 12. September 2026*
`,
        },
        en: {
            title: 'Terms of Service',
            body: `
> **Note:** This is a courtesy translation. The German version is legally binding.

## 1. Scope

These Terms of Service apply to the use of the platform StratSync (accessible at stratsync.pro), operated by Sembach Software Solutions, proprietor Luis Sembach, Winklerstraße 10, 90403 Nuremberg.

## 2. Registration and Account

- Registration is carried out via Steam or Faceit OAuth.
- Each user may only create one account.
- Use of the platform is only permitted from the age of 16. By registering, the user confirms that they are at least 16 years old.
- The user is responsible for securing their login credentials.
- We reserve the right to suspend or delete accounts in the event of suspected misuse.

## 3. Service Description

StratSync provides the following services:
- Team management and communication
- Demo analysis and storage
- Strategy planning on interactive maps
- Utility lineup management
- Game server provisioning (CS2)
- League and season tracking
- Premium membership with enhanced features

## 4. Premium Membership

- StratSync offers Player Premium for one user account, Team Premium for a selected team, and a bundle consisting of Team Premium plus Player Premium for the five Main Roster players displayed at purchase.
- Paid Premium plans are monthly recurring subscriptions processed through Stripe. The total price, currency and billing period displayed in Stripe Checkout before purchase are binding.
- The subscription begins after successful payment confirmation. Stripe provides invoices, payment-method management and subscription management.
- The subscription renews for another month until cancelled. It can be cancelled through the Stripe subscription-management link in StratSync and normally ends after the already paid billing period; termination for cause remains unaffected.
- If a payment fails or is reversed, the affected Premium features may be suspended after the paid period ends.
- Premium codes, points-shop rewards and expressly designated goodwill or test grants are not separate paid subscriptions. Their duration follows the applicable display or grant.
- Statutory withdrawal, warranty and refund rights remain unaffected. Where express consent and acknowledgement are required to begin a digital service before a withdrawal period ends, they will be requested separately during checkout.
- A user can submit a withdrawal through the [“Withdraw from contract”](/withdrawal) function linked beside the subscription information in Account Settings or by another unequivocal declaration to contact@stratsync.pro. The function is also directly available at /withdrawal. No reason is required. Receipt through the electronic function is confirmed electronically without undue delay.

## 5. Consumer withdrawal information

### Right of withdrawal

You have the right to withdraw from this contract within fourteen days without giving any reason. The withdrawal period is fourteen days from the day the contract is concluded.

To exercise this right, inform us — Sembach Software Solutions, proprietor Luis Sembach, Winklerstraße 10, 90403 Nuremberg, Germany, email contact@stratsync.pro — by an unequivocal statement. You may use the electronic [“Withdraw from contract”](/withdrawal) function. Sending the notice before the period expires is sufficient to meet the deadline.

### Effects of withdrawal

If you withdraw, we will reimburse all payments received in connection with the withdrawn contract without undue delay and no later than fourteen days after receiving your notice. We generally use the same payment method as the original transaction unless otherwise agreed, without charging reimbursement fees.

If you expressly requested that the service begin during the withdrawal period, you must pay an appropriate amount for service already provided up to the time of withdrawal where the statutory requirements are met.

### Model withdrawal form

If you wish to withdraw, you may send the following details to contact@stratsync.pro (use of this model is optional): name and address, identification of the Premium plan/team/player account, date of contract conclusion, an unequivocal withdrawal statement, and the date sent.

## 6. User Obligations

The user agrees to:
- Not upload any illegal content
- Not use the platform for automated data collection (scraping)
- Not misuse game servers for purposes outside of CS2
- Respect the rights of third parties (particularly copyrights on demo files)

## 7. Availability

- We strive for high availability but do not guarantee uninterrupted service.
- Maintenance windows will be announced in advance when possible.
- We are not liable for data loss caused by technical failures, unless caused by intentional or grossly negligent conduct.

## 8. Limitation of Liability

- Use is at the user's own risk.
- We are not liable for damages resulting from the use of game servers, demo analysis, or third-party APIs (Steam, Faceit).
- Liability is limited to intentional and grossly negligent conduct.
- In cases of slight negligence, we are only liable for breach of essential contractual obligations, limited to foreseeable, contract-typical damages.

## 9. Termination

- The user may delete their account at any time.
- Paid Premium subscriptions may be cancelled independently of account deletion through the linked Stripe subscription-management page.
- We reserve the right to suspend accounts in the event of a violation of these Terms without prior notice.
- Upon termination, personal data will be deleted in accordance with our Privacy Policy.

## 10. Changes to Terms

- We reserve the right to modify these Terms at any time.
- Changes will be communicated to the user upon their next login.
- Continued use after changes constitutes acceptance.

## 11. Applicable Law and Jurisdiction

- The laws of the Federal Republic of Germany apply.
- The place of jurisdiction, insofar as legally permissible, is the registered office of the operator.

*Last updated: September 12, 2026*
`,
        },
    },
};

/* ═══════════════════════════════════════════
   COMPONENT
   ═══════════════════════════════════════════ */

export function LegalPage() {
    const location = useLocation();
    const { i18n } = useTranslation();

    // Determine type from path
    const path = location.pathname.replace(/^\//, '');
    const type: LegalType = (path === 'imprint' || path === 'privacy' || path === 'terms') ? path : 'imprint';

    // Language: default to app language, allow manual toggle
    const appLang = i18n.language?.startsWith('de') ? 'de' : 'en';
    const [lang, setLang] = useState<'de' | 'en'>(appLang);

    const content = LEGAL_CONTENT[type][lang];
    const otherLinks: { path: string; labelDe: string; labelEn: string }[] = [
        { path: '/imprint', labelDe: 'Impressum', labelEn: 'Imprint' },
        { path: '/privacy', labelDe: 'Datenschutz', labelEn: 'Privacy Policy' },
        { path: '/terms', labelDe: 'AGB', labelEn: 'Terms of Service' },
    ].filter(l => l.path !== `/${type}`);

    return (
        <div className="legal-page">
            <div className="legal-bg" />
            <div className="legal-container">
                {/* Header */}
                <div className="legal-header">
                    <Link to="/login" className="legal-back">
                        <ArrowLeft size={18} />
                        <span>StratSync</span>
                    </Link>
                    <button className="legal-lang-toggle" onClick={() => setLang(l => l === 'de' ? 'en' : 'de')}>
                        <Globe size={14} />
                        {lang === 'de' ? 'English' : 'Deutsch'}
                    </button>
                </div>

                {/* Binding notice for EN */}
                {lang === 'en' && (
                    <div className="legal-notice">
                        This is a courtesy translation. The <button onClick={() => setLang('de')}>German version</button> is legally binding.
                    </div>
                )}

                {/* Card */}
                <div className="legal-card">
                    <h1 className="legal-title">{content.title}</h1>
                    <div className="legal-body" dangerouslySetInnerHTML={{ __html: markdownToHtml(content.body) }} />
                </div>

                {/* Nav to other legal pages */}
                <div className="legal-nav">
                    {otherLinks.map(l => (
                        <Link key={l.path} to={l.path} className="legal-nav-link">
                            {lang === 'de' ? l.labelDe : l.labelEn}
                        </Link>
                    ))}
                </div>

                {/* Footer */}
                <div className="legal-footer">
                    © {new Date().getFullYear()} StratSync. All rights reserved.
                </div>
            </div>
        </div>
    );
}

/* ── Minimal markdown→HTML converter (no deps) ── */
export function markdownToHtml(md: string): string {
    return md
        // Tables
        .replace(/^\|(.+)\|\s*$/gm, (_, row) => {
            const cells = row.split('|').map((c: string) => c.trim());
            if (cells.every((c: string) => /^-+$/.test(c))) return ''; // separator row
            const tag = cells.every((c: string) => /^-+$/.test(c)) ? 'th' : 'td';
            return `<tr>${cells.map((c: string) => `<${tag}>${c}</${tag}>`).join('')}</tr>`;
        })
        .replace(/(<tr>.*<\/tr>\n?)+/g, (block) => `<table>${block}</table>`)
        // Headers
        .replace(/^#### (.+)$/gm, '<h4>$1</h4>')
        .replace(/^### (.+)$/gm, '<h3>$1</h3>')
        .replace(/^## (.+)$/gm, '<h2>$1</h2>')
        .replace(/^# (.+)$/gm, '<h1>$1</h1>')
        // Blockquotes
        .replace(/^> (.+)$/gm, '<blockquote>$1</blockquote>')
        // Bold + Italic
        .replace(/\*\*(.+?)\*\*/g, '<strong>$1</strong>')
        .replace(/\*(.+?)\*/g, '<em>$1</em>')
        // Links
        .replace(/\[([^\]]+)\]\(([^)]+)\)/g, '<a href="$2" target="_blank" rel="noopener noreferrer">$1</a>')
        // Unordered lists
        .replace(/^- (.+)$/gm, '<li>$1</li>')
        .replace(/(<li>.*<\/li>\n?)+/g, (block) => `<ul>${block}</ul>`)
        // Horizontal rules
        .replace(/^---$/gm, '<hr />')
        // Paragraphs (double newlines)
        .replace(/\n\n/g, '</p><p>')
        .replace(/^/, '<p>')
        .replace(/$/, '</p>')
        // Clean up empty paragraphs
        .replace(/<p>\s*<\/p>/g, '')
        .replace(/<p>(<h[1-4]>)/g, '$1')
        .replace(/(<\/h[1-4]>)<\/p>/g, '$1')
        .replace(/<p>(<table>)/g, '$1')
        .replace(/(<\/table>)<\/p>/g, '$1')
        .replace(/<p>(<ul>)/g, '$1')
        .replace(/(<\/ul>)<\/p>/g, '$1')
        .replace(/<p>(<blockquote>)/g, '$1')
        .replace(/(<\/blockquote>)<\/p>/g, '$1')
        .replace(/<p>(<hr \/>)/g, '$1')
        .replace(/(<hr \/>)<\/p>/g, '$1');
}
